import React, { CSSProperties, useImperativeHandle, useRef } from "react";
import { useClient } from "../../../hooks/useClient";
import { ActiveBox } from "../../active-box";
import { BottomMenuPanel } from "../../bottom-menu-panel";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../bottom-menu/buttons";
import { PlaceholderImage } from "../../placeholder";

import "./background-pattern-panel.css";

export interface IBackgroundPatternsPanelRef {
	scrollIntoView(animate?: boolean): void;
}
const BackgroundPatternsThumb = ({
	patternThumb,
	isActive,
	onClick,
	style,
}: {
	patternThumb: string | undefined;
	isActive?: boolean;
	onClick?(): void;
	style: CSSProperties;
}) => (
	<ActiveBox className="patterns-panel__img-container" isActive={isActive}>
		<PlaceholderImage
			onClick={() => {
				if (onClick) onClick();
			}}
			className="patterns-panel__img"
			src={patternThumb}
			loading="lazy"
			style={style}
		/>
	</ActiveBox>
);

interface IBackgroundPatternPanelProps {
	patterns: readonly eXpress.core.StockBackground[];
	aspectRatio: number;
	selectedPatternBackground: eXpress.core.StockBackground | undefined;
	onClick?(patternBackground: eXpress.core.StockBackground): void;
}

export function BackgroundPatternPanel({
	patterns,
	aspectRatio,
	selectedPatternBackground,
	onClick,
	ref,
}: IBackgroundPatternPanelProps & { ref?: React.Ref<IBackgroundPatternsPanelRef> }) {
	const client = useClient();

	const bottomMenuRef = useRef<IBottomMenuButtonsRef>(null);

	useImperativeHandle(
		ref,
		() => ({
			scrollIntoView: (animate = true) => {
				if (!bottomMenuRef.current) return;
				const index = selectedPatternBackground
					? patterns.indexOf(selectedPatternBackground)
					: -1;
				bottomMenuRef.current.scrollIntoView(index, {
					animate,
				});
			},
		}),
		[patterns, selectedPatternBackground],
	);

	return (
		<BottomMenuPanel className="patterns-panel">
			<BottomMenuButtons scrollableClassName="patterns-panel__items" ref={bottomMenuRef}>
				{patterns.map((pattern) => (
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events
					<div
						key={pattern.key}
						role="button"
						tabIndex={0}
						className="patterns-panel__img-clickable"
						onClick={(e) => {
							e.stopPropagation();
							if (onClick) onClick(pattern);
						}}
					>
						<BackgroundPatternsThumb
							key={pattern.key}
							isActive={
								selectedPatternBackground?.code
									? selectedPatternBackground.code === pattern.code
									: false
							}
							patternThumb={pattern.thumbUrl?.getAbsoluteUrl(client)}
							style={{
								aspectRatio,
							}}
						/>
					</div>
				))}
			</BottomMenuButtons>
		</BottomMenuPanel>
	);
}
